/**
 * Pure helpers behind VideoRunsTable / VideoRunRow / RunHistoryDialog (M4 F13, F19, F20):
 * sections of runs per analysis group, the actions each run allows given the source's pointer
 * summary and the group pin, and the small formatters of the run rows. No Vue, no services.
 *
 * Inputs are ledger run documents (`_id`, `status`, `group_id`, `batch`, `attempt`, ...), the
 * `GET /analysis-runs/summary` item of the source (`active_run_id`, `active_run_pinned`,
 * `attempts`, `last_run`) and the `GET /analysis-groups/membership` groups of the source
 * (`group_id`, `name`, `member_id`, `pinned_run_id`, `resolution`).
 */
import { normalizeRunStatus } from '@/store/modules/analysisQueue';
import { runIdOf } from '@/utils/runLabels';

export const UNGROUPED_SECTION_KEY = 'ungrouped';
export const UNGROUPED_SECTION_LABEL = 'Direct runs';

const LIVE_STATUSES = Object.freeze(['QUEUED', 'DISPATCHED', 'RUNNING', 'CANCEL_REQUESTED']);
const REMOVABLE_STATUSES = Object.freeze(['QUEUED', 'DISPATCHED']);

export function isLiveStatus(status) {
  return LIVE_STATUSES.includes(normalizeRunStatus(status));
}

export function isSucceeded(run) {
  return normalizeRunStatus(run?.status) === 'SUCCEEDED';
}

function toMillis(value) {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest first: enqueued_at desc, then attempt desc. */
export function sortRunsNewestFirst(runs) {
  return [...(Array.isArray(runs) ? runs.filter(Boolean) : [])].sort((a, b) => {
    const delta = toMillis(b.enqueued_at || b.started_at) - toMillis(a.enqueued_at || a.started_at);
    if (delta !== 0) return delta;
    return (Number(b.attempt) || 0) - (Number(a.attempt) || 0);
  });
}

/**
 * Splits the runs of one source into sections: the direct (ungrouped) runs first, then one
 * section per group the source belongs to (membership order), then groups that only appear on
 * runs (membership removed or unknown). Empty membership sections are kept so the row can show
 * 'No runs in this group yet' with its resolution chip.
 *
 * @param {object[]} runs ledger docs of the source
 * @param {{ memberships?: object[], groups?: object[] }} ctx membership entries of the source
 *   and the full groups list (names for runs whose group is not a membership any more)
 * @returns {Array<{ key: string, group_id: string|null, name: string, membership: object|null,
 *   runs: object[] }>}
 */
export function groupRunsBySection(runs, { memberships = [], groups = [] } = {}) {
  const sorted = sortRunsNewestFirst(runs);
  const byGroup = new Map();
  sorted.forEach((run) => {
    const key = run.group_id == null || run.group_id === '' ? null : String(run.group_id);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(run);
  });

  const sections = [
    {
      key: UNGROUPED_SECTION_KEY,
      group_id: null,
      name: UNGROUPED_SECTION_LABEL,
      membership: null,
      runs: byGroup.get(null) || [],
    },
  ];
  const seen = new Set();
  (Array.isArray(memberships) ? memberships : []).forEach((membership) => {
    if (!membership || membership.group_id == null) return;
    const id = String(membership.group_id);
    if (seen.has(id)) return;
    seen.add(id);
    sections.push({
      key: `group-${id}`,
      group_id: id,
      name: membership.name || groupNameOf(groups, id),
      membership,
      runs: byGroup.get(id) || [],
    });
  });
  byGroup.forEach((groupRuns, id) => {
    if (id === null || seen.has(id)) return;
    seen.add(id);
    sections.push({
      key: `group-${id}`,
      group_id: id,
      name: groupNameOf(groups, id),
      membership: null,
      runs: groupRuns,
    });
  });
  return sections;
}

export function groupNameOf(groups, groupId) {
  const found = (Array.isArray(groups) ? groups : []).find(
    (group) => group && String(group.id) === String(groupId)
  );
  return found?.name || `Group #${groupId}`;
}

/**
 * Actions a run row offers. `open` needs a result (SUCCEEDED); `promote` / `pin` / `unpin`
 * operate on the source pointer; `cancel` (RUNNING) and `remove` (QUEUED | DISPATCHED) on the
 * live run; `pinInGroup` / `unpinInGroup` on the membership of the run's group (F19).
 *
 * @param {object} run ledger doc
 * @param {{ activeRunId?: string|null, activePinned?: boolean, groupPinnedRunId?: string|null,
 *   succeededCount?: number }} ctx
 */
export function runActionsFor(
  run,
  { activeRunId = null, activePinned = false, groupPinnedRunId = null, succeededCount = 0 } = {}
) {
  const id = runIdOf(run);
  const status = normalizeRunStatus(run?.status);
  const succeeded = status === 'SUCCEEDED';
  const isActive = Boolean(id) && id === (activeRunId == null ? null : String(activeRunId));
  const inGroup = run?.group_id != null && run?.group_id !== '';
  const groupPinned = inGroup && Boolean(id) && id === String(groupPinnedRunId ?? '');
  return {
    open: succeeded,
    compare: succeeded && succeededCount > 1,
    promote: succeeded && !isActive,
    pin: succeeded && !(isActive && activePinned),
    unpin: succeeded && isActive && Boolean(activePinned),
    cancel: status === 'RUNNING',
    remove: REMOVABLE_STATUSES.includes(status),
    pinInGroup: succeeded && inGroup && !groupPinned,
    unpinInGroup: groupPinned,
    isActive,
    isGroupPinned: groupPinned,
  };
}

/** '71% · 3 alerts' from the ledger result summary when the worker stored one, else '—'. */
export function resultOf(run) {
  const summary = run?.result_summary || run?.result || null;
  if (!summary || typeof summary !== 'object') return '—';
  const parts = [];
  const match = summary.global_match_pct ?? summary.global_match ?? summary.match_pct;
  if (match != null) parts.push(`${match}%`);
  const alerts = summary.alerts ?? summary.alerts_total;
  if (alerts != null) parts.push(`${alerts} alerts`);
  return parts.length ? parts.join(' · ') : '—';
}

export function durationOf(run) {
  if (!run?.started_at || !run?.finished_at) return '—';
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes} m ${String(seconds).padStart(2, '0')} s` : `${seconds} s`;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/** 'Run #3' or 'Run' when the attempt is unknown. */
export function runTitle(run) {
  const attempt = Number(run?.attempt);
  return Number.isFinite(attempt) && attempt > 0 ? `Run #${attempt}` : 'Run';
}

/**
 * Ledger-driven status of a source for list rows (F20): the last run when it is live or ended
 * badly (Queued / Running / Failed / Cancelled), null when the last run succeeded or there is
 * no run, so the SQL status chip stays the single source of truth in the happy path.
 * @param {object|null} summary summary item of the source
 * @returns {{ status: string, run_id: string|null }|null}
 */
export function ledgerStatusOf(summary) {
  const last = summary?.last_run;
  if (!last) return null;
  const status = normalizeRunStatus(last.status);
  if (!status || status === 'SUCCEEDED') return null;
  return { status, run_id: last.run_id || null };
}

/** True when the ledger knows at least one run for the source (Run history is worth opening). */
export function hasRunHistory(summary) {
  return Boolean(summary && (Number(summary.attempts) > 0 || summary.active_run_id));
}

/** Membership entry of one group among the source's memberships (string/number tolerant). */
export function membershipOf(memberships, groupId) {
  if (groupId == null) return null;
  return (
    (Array.isArray(memberships) ? memberships : []).find(
      (membership) => membership && String(membership.group_id) === String(groupId)
    ) || null
  );
}
